import { doppeActionTypeDefineMetadata } from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
} from '../../client-server-common';
import { wixMediaResourceReplaceExternalImage } from '@wix/devzai-common-wix';
import type { RichTextContent, HideableValue, Values } from '@wix/devzai-utils-common';
import {
    arrayRemoveNullValues,
    immerProduce,
    hideableValueCreateHidden,
    hideableValueGetVisibleValue,
    uuidCreate,
} from '@wix/devzai-utils-common';
import type { DoppeActionContentSectionsSettings } from '../../client-server-common/types/doppe-action-content-sections-settings';
import { doppeActionContentSectionSettingsDefaultValuesMigration } from '../../client-server-common/types/doppe-action-content-sections-settings';
import type { DoppeActionCtaButtonsSettings } from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import { doppeActionCtaButtonsSettingsDefaultValuesMigration } from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    DoppeListActionSettings,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionListItemSettingsCreateDefault,
} from '../../client-server-common/types/doppe-list-action-settings';
import {
    doppeListActionContentSettingsMetadataCreateForListActionCommonSettings,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
} from '../../client-server-common/bymo-page-props-metadata/bymo-page-props-metadata';

/**
 * Visual prefix shown before each FAQ item's title in the widget.
 * Phase 1: constant designs only — no user-pickable emojis or images.
 */
export const DoppeActionTypeFaqPrefixStyle = {
    None: 'none',
    Bullet: 'bullet',
    Number: 'number',
    Icon: 'icon',
} as const;
export type DoppeActionTypeFaqPrefixStyle = Values<typeof DoppeActionTypeFaqPrefixStyle>;

/**
 * Accordion behaviour for the FAQ items list.
 * - `Single`: opening an item auto-closes any other open item.
 * - `Multi`:  any number of items can be open simultaneously.
 */
export const DoppeActionTypeFaqAccordionMode = {
    Single: 'single',
    Multi: 'multi',
} as const;
export type DoppeActionTypeFaqAccordionMode = Values<typeof DoppeActionTypeFaqAccordionMode>;

/**
 * Glyph chosen as the per-item prefix when `faqPrefixStyle === 'icon'`. Each value
 * maps to a Unicode symbol rendered by the widget — sourced from the design's
 * "Inner / Symbols" component variants.
 */
export const DoppeActionTypeFaqIconChoice = {
    Star: 'star',         // ★
    Sparkle: 'sparkle',   // ✸
    Diamond: 'diamond',   // ✦
    Asterisk: 'asterisk', // ❋
    Flower: 'flower',     // ✿
    Heart: 'heart',       // ❤
    Check: 'check',       // ✓
    Arrow: 'arrow',       // →
} as const;
export type DoppeActionTypeFaqIconChoice = Values<typeof DoppeActionTypeFaqIconChoice>;

/**
 * The actual glyph rendered for each `DoppeActionTypeFaqIconChoice`. Kept as a
 * plain map so the widget can resolve the glyph without an extra switch.
 */
export const doppeActionTypeFaqIconChoiceGlyphMap: Record<DoppeActionTypeFaqIconChoice, string> = {
    [DoppeActionTypeFaqIconChoice.Star]: `★`,
    [DoppeActionTypeFaqIconChoice.Sparkle]: `✸`,
    [DoppeActionTypeFaqIconChoice.Diamond]: `✦`,
    [DoppeActionTypeFaqIconChoice.Asterisk]: `❋`,
    [DoppeActionTypeFaqIconChoice.Flower]: `✿`,
    [DoppeActionTypeFaqIconChoice.Heart]: `❤`,
    [DoppeActionTypeFaqIconChoice.Check]: `✓`,
    [DoppeActionTypeFaqIconChoice.Arrow]: `→`,
};

export namespace DoppeActionTypeFaq {

    /**
     * A single FAQ question. Extends the standard list-item base settings so the
     * item participates in per-item analytics (views / clicks), visibility, and
     * delete-tracking just like other list actions (downloads, properties, links).
     */
    export interface FaqItemSettings extends DoppeListActionSettings.ListItemBaseSettings {
        title: string;
        description: string;
    }

    /**
     * FAQ is a list action — `listDataItems` holds the questions, and per-item
     * analytics flow through the standard list-action pipeline. The editing UX
     * remains custom-inline (see `DoppeActionTypeFaqActionSettingsPanel`); only
     * the data shape is list-action-conformant.
     */
    export interface ActionSettings extends
        DoppeListActionSettings<FaqItemSettings>,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithSearchSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {

        /** Action-level description rendered above the questions list. */
        description: HideableValue<RichTextContent>;

        faqPrefixStyle: DoppeActionTypeFaqPrefixStyle;
        faqAccordionMode: DoppeActionTypeFaqAccordionMode;
        /**
         * Which glyph the widget renders when `faqPrefixStyle === 'icon'`. Ignored
         * for other prefix styles, but always persisted so toggling the prefix style
         * back to Icon restores the user's last choice.
         */
        faqIconChoice: DoppeActionTypeFaqIconChoice;
    }
}

/**
 * Module-level factory for new FAQ items. Lives outside the namespace because
 * `local/no-namespace-values` forbids functions inside namespaces.
 */
export function doppeActionTypeFaqCreateNewItem(
    options: {
        title: string;
        description: string;
    }
): DoppeActionTypeFaq.FaqItemSettings {
    return {
        ...doppeListActionListItemSettingsCreateDefault(uuidCreate()),
        title: options.title,
        description: options.description,
    };
}

/**
 * Default-values migration for the FAQ-specific fields. Composed alongside the
 * standard list-action / sections / CTA migrations inside `resolveActionSettings`.
 */
export function doppeActionTypeFaqDefaultValuesMigration(
    settings: Partial<DoppeActionTypeFaq.ActionSettings>
): Pick<DoppeActionTypeFaq.ActionSettings, 'faqPrefixStyle' | 'faqAccordionMode' | 'faqIconChoice'> {
    return {
        faqPrefixStyle: settings.faqPrefixStyle ?? DoppeActionTypeFaqPrefixStyle.Number,
        faqAccordionMode: settings.faqAccordionMode ?? DoppeActionTypeFaqAccordionMode.Single,
        faqIconChoice: settings.faqIconChoice ?? DoppeActionTypeFaqIconChoice.Star,
    };
}

/**
 * Per-item content metadata — declares which item fields are user-facing content
 * so AI / translation / analytics introspection can find them. Framework fields
 * from `ListItemBaseSettings` (id/enabled/deleted/displayInSearch/isDummy) are
 * handled by the list-action metadata machinery; we only declare item content here.
 */
export const doppeActionTypeFaqActionListItemContentSettingsMetadata = {
    title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
};

/**
 * Action-level content metadata — composed from the canonical list-action helpers
 * (`mainTitle`/`mainImage` from WithMainDetails; `itemShow*` from CommonSettings)
 * plus the FAQ-specific action-level description.
 */
export const doppeActionTypeFaqContentSettingsMetadata = {
    ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails(),
    ...doppeListActionContentSettingsMetadataCreateForListActionCommonSettings(),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
};

export const doppeActionTypeFaq = doppeActionTypeDefineMetadata<DoppeActionTypeFaq.ActionSettings>({
    id: 'io.bymo.action/faq',
    resolveActionSettings: action => {
        const settings = action.settings as Partial<DoppeActionTypeFaq.ActionSettings>;
        // Backfill ListItemBaseSettings defaults onto every item. Factory-created
        // items already carry the full base settings, but AI-generated items only
        // provide `{id, title, description}` — without `enabled`/`deleted`/
        // `displayInSearch`/`isDummy` the standard
        // `doppeListActionSettingsFilterHiddenListItemsFromActionView` filter rejects
        // them all (treats `enabled: undefined` as not-enabled) and the widget renders
        // an empty questions section. Defaults first, user fields second.
        const migratedListDataItems = settings.listDataItems?.map(sourceItem => ({
            ...doppeListActionListItemSettingsCreateDefault(sourceItem.id ?? uuidCreate()),
            ...sourceItem,
        }));
        // Strip the fields whose migrated form differs from the raw input —
        // otherwise the trailing `...restSettings` spread would clobber the
        // migrations established above.
        const {
            listDataItems: _listDataItems,
            ctaButtons: _ctaButtons,
            sections: _sections,
            ...restSettings
        } = settings;
        return {
            description: hideableValueCreateHidden(''),
            ...doppeListActionGetDefaultListSettings<DoppeActionTypeFaq.FaqItemSettings>({
                listDataItems: migratedListDataItems,
            }),
            ...doppeListActionGetDefaultSearchListSettings(settings),
            ...doppeListActionGetMainDetailsDefaultSettings({}, {
                mainTitle: settings.mainTitle,
                mainImage: settings.mainImage,
            }),
            ...doppeActionTypeFaqDefaultValuesMigration(restSettings),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...restSettings,
        };
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeFaqContentSettingsMetadata,
        supportsActionCtaButtons: { customCtaButtonTypes: {} },
        supportsContentSections: { customContentSectionsTypes: {} },
        hideableContentSettingsNames: { mainTitle: true, description: true, mainImage: true },
        bindableContentSettingsNames: {},
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeFaqActionListItemContentSettingsMetadata,
        // FAQ items don't have per-item CTA buttons — the action-level CTA buttons
        // surface separately (e.g. "Try It For Free" below the questions list).
        supportsActionCtaButtons: false,
        // FAQ items don't have per-item sections — sections live at action level.
        supportsContentSections: false,
        hideableContentSettingsNames: {},
        bindableContentSettingsNames: {},
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    resolveUsedMedia: actionSettings => arrayRemoveNullValues([hideableValueGetVisibleValue(actionSettings.mainImage, null)]),
    resolveMainMedia: actionSettings => hideableValueGetVisibleValue(actionSettings.mainImage, null),
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) =>
        immerProduce(actionSettings, draft => {
            const currentMainImage = hideableValueGetVisibleValue(draft.mainImage, null);
            if (currentMainImage) {
                draft.mainImage = hideableValueCreateHidden(
                    wixMediaResourceReplaceExternalImage(currentMainImage, externalImagesUrlToImageResourceMap)
                );
            }
        }),
    // Surface FAQ items in the page-level search so visitors can find a question by
    // its title. The search converter (`converActionListItemToSearchItemViewer`)
    // matches against `title` and ignores `description`/`image`/`url`, so unused
    // fields are seeded with empty defaults. `displayInSearch` per-item gives
    // creators control over which questions appear in results.
    resolveListItems: action => {
        return action.settings.listDataItems.map(item => ({
            url: null,
            title: item.title,
            action: action,
            couponCode: hideableValueCreateHidden(''),
            description: hideableValueCreateHidden(''),
            image: null,
            itemId: item.id,
            ribbon: hideableValueCreateHidden(''),
            enabled: item.enabled,
            deleted: item.deleted,
            displayInSearch: item.displayInSearch,
        }));
    },
    resolveListSearchSettings: actionSettings => actionSettings.listSearchSettings,
});
